import {createFeatureSelector, createSelector} from '@ngrx/store';
import {SharedState} from './shared.reducer';
import {IStore} from '../../../shared/shared-models/store/store';
import {IMessage, IMessageAutoOrderUpdate, MessageTypesEnum} from '../models';
import {readOperationalEventTimestampForUi} from '../../../shared/shared-utils/firestore/operational-server-timestamp.utils';
import {selectUser} from '../../user-access/user-store/user.selectors';
import {IUser} from '../../user-access/models';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import {IAutoOrderOrder, IAutoOrderOrderSettings} from '../../auto-ordering/models';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';
import {IDebtorDetails} from '../../quotes/models/debtors';

export const selectSharedState = createFeatureSelector<SharedState>('core-shared');

// ====================================================================================================
// Suppliers
// ====================================================================================================
export const selectIsSuppliersLoading = createSelector(
  selectSharedState,
  (state: SharedState) => state.isSuppliersLoading,
);

export const selectSuppliersByUserSelectedStore = createSelector(
  selectSharedState,
  selectSelectedStore,
  (state: SharedState, selectedStore: IStore): Record<string, ISupplier> => state.suppliers[selectedStore.storeId] || <Record<string, ISupplier>>{},
);

// ====================================================================================================
// Debtors
// ====================================================================================================
export const selectIsDebtorsLoading = createSelector(
  selectSharedState,
  (state: SharedState) => state.isDebtorsLoading,
);

export const selectDebtorsByUserSelectedStore = createSelector(
  selectSharedState,
  selectSelectedStore,
  (state: SharedState, selectedStore: IStore): Record<string, IDebtorDetails> => state?.debtors?.[selectedStore?.storeId] ?? {},
);

export const selectDepartmentsBySelectedStore = createSelector(
  selectSharedState,
  selectSelectedStore,
  (state: SharedState, selectedStore: IStore) => state.departments[selectedStore.storeId] || [],
);


export const selectLineColoursBySelectedStore = createSelector(
  selectSharedState,
  selectSelectedStore,
  (state: SharedState, selectedStore: IStore) => state.lineColours[selectedStore.storeId] || [],
);


// ====================================================================================================
// Colleagues
// ====================================================================================================
//#region Get Colleagues
export const selectColleagues = createSelector(
  selectSharedState,
  (state: SharedState) => state.colleagues,
);

export const selectIsColleaguesLoading = createSelector(
  selectSharedState,
  (state: SharedState) => state.isColleaguesLoading,
);

//endregion

// ====================================================================================================
// Storage
// ====================================================================================================

export const selectStorageUrl = createSelector(
  selectSharedState,
  (state: SharedState) => state.storageUrl,
);

export const selectCurrentVersion = createSelector(
  selectSharedState,
  (state: SharedState) => state.currentVersion,
);

// ====================================================================================================
// Messages From App
// ====================================================================================================

export const selectMessagesFromAppAutoOrderUpdate = createSelector(
  selectSharedState,
  selectSelectedStore,
  (state: SharedState, store) => {
    if (!state.messagesFromApp[store.storeId]?.[MessageTypesEnum.AUTO_ORDERS_U_UPDATE]) {
      return [] as IMessage<IMessageAutoOrderUpdate[]>[];
    }
    return state.messagesFromApp[store.storeId][MessageTypesEnum.AUTO_ORDERS_U_UPDATE] || [] as IMessage<IMessageAutoOrderUpdate[]>[];
  },
);

export const selectMessagesFromAppAutoOrderUpdateAsOrders = createSelector(
  selectMessagesFromAppAutoOrderUpdate,
  selectUser,
  (messages: IMessage<IMessageAutoOrderUpdate[]>[], user: IUser) => {
    const orders: IAutoOrderOrder[] = [];
    messages.forEach((message: IMessage<IMessageAutoOrderUpdate[]>) => {
      Object.keys(message.payload.data).forEach((supplierId: string): void => {
        const settings: IAutoOrderOrderSettings = message.payload.data[supplierId] as unknown as IAutoOrderOrderSettings;
        const order: IAutoOrderOrder = {
          userId: user.id,
          owned: user.id,
          supplierId: supplierId,
          supplierName: '',
          generated: readOperationalEventTimestampForUi(message?.timestamp),
          orderTotal: 0,
          orderId: 'Created Order',
          status: 'CREATED',
          pdfStatus: 'READY',
          emailStatus: 'READY',
          iqStatus: 'READY',
          scheduled: 'SCHEDULED',
          errorMsg: null,
          orderItems: [],
          createdSequence: -1,
          purchaseOrderNo: 'Not Submitted',
          orderSettings: {
            ...settings,
          },
        };
        orders.push(order);
      });
    });
    return orders;
  },
);
